import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Converte string datetime-local (YYYY-MM-DDTHH:mm) para Date interpretando como hora local
 * Isso evita problemas de timezone com Supabase
 * Se o horário for omitido, usa 00:00 (meia-noite)
 */
export function parseDateTimeLocal(dateTimeString: string): Date {
  if (!dateTimeString) {
    throw new Error('Data não pode estar vazia');
  }

  let year: number, month: number, day: number, hour: number = 0, minute: number = 0;

  try {
    if (dateTimeString.includes('T')) {
      // Formato: YYYY-MM-DDTHH:mm
      const [datePart, timePart] = dateTimeString.split('T');
      const dateParts = datePart.split('-');
      
      if (dateParts.length !== 3) throw new Error('Formato de data inválido');
      
      year = parseInt(dateParts[0], 10);
      month = parseInt(dateParts[1], 10);
      day = parseInt(dateParts[2], 10);

      if (timePart) {
        const timeParts = timePart.split(':');
        if (timeParts.length >= 2) {
          hour = parseInt(timeParts[0], 10);
          minute = parseInt(timeParts[1], 10);
        }
      }
    } else {
      // Formato: YYYY-MM-DD (apenas data)
      const dateParts = dateTimeString.split('-');
      if (dateParts.length !== 3) throw new Error('Formato de data inválido');
      
      year = parseInt(dateParts[0], 10);
      month = parseInt(dateParts[1], 10);
      day = parseInt(dateParts[2], 10);
    }

    // Validar valores
    if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hour) || isNaN(minute)) {
      throw new Error('Valores de data/hora inválidos');
    }

    if (month < 1 || month > 12 || day < 1 || day > 31 || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      throw new Error('Valores de data/hora fora do intervalo válido');
    }

    // Criar data com valores locais (não UTC)
    const date = new Date(year, month - 1, day, hour, minute, 0, 0);

    // Validar se a data resultante é válida
    if (isNaN(date.getTime())) {
      throw new Error('Data resultante é inválida');
    }

    return date;
  } catch (error) {
    console.error('Erro ao fazer parse da data:', dateTimeString, error);
    throw new Error(`Data inválida: ${dateTimeString}`);
  }
}

/**
 * Formata data para datetime-local format (YYYY-MM-DDTHH:mm)
 */
export function formatToDateTimeLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Formata data em ISO string sem timezone (armazena como está localmente)
 * Para Supabase: YYYY-MM-DDTHH:mm:ss
 */
export function formatToISOLocalString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}
